import { Link } from 'react-router-dom'
import NotFound from '../NotFound'
import { SiteFooter, SiteHeader, SiteLink } from './SiteChrome'
import { useScrollToAnchor } from './useScrollToAnchor'
import { sitePageByPath, type PageAction, type PageBlock, type SitePage } from './sitePages'

function ActionLink({ action }: { action: PageAction }) {
  const className =
    action.variant === 'outline'
      ? 'inline-flex min-h-12 items-center justify-center rounded-full border border-aan-bright px-7 font-display text-xs font-black uppercase tracking-[0.14em] text-aan-bright transition hover:bg-aan-bright hover:text-black'
      : 'inline-flex min-h-12 items-center justify-center rounded-full bg-aan-bright px-7 font-display text-xs font-black uppercase tracking-[0.14em] text-black transition hover:bg-white'

  if (action.external) {
    return (
      <a className={className} href={action.href} rel="noreferrer" target="_blank">
        {action.label}
      </a>
    )
  }

  return <SiteLink className={className} href={action.href}>{action.label}</SiteLink>
}

function Block({ block }: { block: PageBlock }) {
  if (block.kind === 'text') {
    return (
      <section className="scroll-mt-28" id={block.id}>
        {block.heading ? (
          <h2 className="font-display text-3xl font-black uppercase leading-none text-white sm:text-4xl">
            {block.heading}
          </h2>
        ) : null}
        <div className={`space-y-5 text-base leading-8 text-white/80 ${block.heading ? 'mt-6' : ''}`}>
          {block.paragraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
      </section>
    )
  }

  if (block.kind === 'list') {
    return (
      <section className="scroll-mt-28" id={block.id}>
        <h2 className="font-display text-3xl font-black uppercase leading-none text-white sm:text-4xl">
          {block.heading}
        </h2>
        {block.intro ? <p className="mt-6 text-base leading-8 text-white/80">{block.intro}</p> : null}
        <ul className="mt-6 space-y-4">
          {block.items.map((item) => (
            <li className="flex gap-4 text-base leading-7 text-white/80" key={item}>
              <span aria-hidden className="mt-2.5 h-2 w-2 shrink-0 rounded-full bg-aan-bright" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </section>
    )
  }

  if (block.kind === 'cards') {
    return (
      <section className="scroll-mt-28" id={block.id}>
        <h2 className="font-display text-3xl font-black uppercase leading-none text-white sm:text-4xl">
          {block.heading}
        </h2>
        {block.intro ? <p className="mt-6 text-base leading-8 text-white/80">{block.intro}</p> : null}
        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          {block.cards.map((card) => (
            <div
              className="rounded-[12px] border border-white/15 bg-neutral-950 p-6 transition hover:border-aan-bright"
              key={card.title}
            >
              <h3 className="font-display text-lg font-black uppercase leading-tight text-aan-bright">
                {card.title}
              </h3>
              <p className="mt-3 text-sm leading-7 text-white/75">{card.body}</p>
            </div>
          ))}
        </div>
      </section>
    )
  }

  return (
    <section className="scroll-mt-28" id={block.id}>
      <h2 className="font-display text-3xl font-black uppercase leading-none text-white sm:text-4xl">
        {block.heading}
      </h2>
      {block.intro ? <p className="mt-6 text-base leading-8 text-white/80">{block.intro}</p> : null}
      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        {block.links.map((link) => (
          <SiteLink
            className="group flex flex-col rounded-[12px] border border-white/15 bg-neutral-950 p-6 transition hover:-translate-y-0.5 hover:border-aan-bright"
            href={link.href}
            key={link.label}
          >
            <h3 className="font-display text-lg font-black uppercase leading-tight text-white transition group-hover:text-aan-bright">
              {link.label}
            </h3>
            <p className="mt-3 text-sm leading-7 text-white/70">{link.description}</p>
            <span className="mt-4 font-display text-[11px] font-black uppercase tracking-[0.18em] text-aan-bright">
              Learn more
            </span>
          </SiteLink>
        ))}
      </div>
    </section>
  )
}

export function SitePageBody({ page }: { page: SitePage }) {
  return (
    <>
      <section className="border-b border-white/10 bg-black px-6 pb-14 pt-12 sm:pb-16 sm:pt-16">
        <div className="mx-auto max-w-[1080px]">
          <nav aria-label="Breadcrumb" className="font-display text-[11px] font-black uppercase tracking-[0.24em] text-white/45">
            <Link className="transition hover:text-aan-bright" to="/">
              Home
            </Link>
            <span className="px-2">/</span>
            <span className="text-aan-bright">{page.eyebrow}</span>
          </nav>
          <h1 className="mt-6 font-display text-4xl font-black uppercase leading-none text-white sm:text-5xl lg:text-6xl">
            {page.title}
          </h1>
          <p className="mt-7 max-w-3xl text-lg leading-8 text-white/75">{page.lede}</p>
        </div>
      </section>

      {page.image ? (
        <div className="bg-black px-6 pt-12 sm:pt-16">
          <img
            className="mx-auto aspect-[16/7] w-full max-w-[1080px] rounded-[12px] object-cover"
            src={page.image}
            alt={page.imageAlt || page.title}
          />
        </div>
      ) : null}

      <div className="mx-auto max-w-[1080px] space-y-14 px-6 py-14 sm:space-y-16 sm:py-16 lg:py-20">
        {page.blocks.map((block, index) => (
          <Block block={block} key={block.id || `${block.kind}-${index}`} />
        ))}
      </div>

      {page.cta ? (
        <section className="px-6 pb-16 sm:pb-20">
          <div className="mx-auto max-w-[1080px] rounded-[12px] border border-aan-bright bg-aan-footer px-7 py-10 text-center shadow-glow sm:px-12 sm:py-12">
            <h2 className="font-display text-3xl font-black uppercase leading-none text-white sm:text-4xl">
              {page.cta.heading}
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-white/75">{page.cta.body}</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              {page.cta.actions.map((action) => (
                <ActionLink action={action} key={action.label} />
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </>
  )
}

export default function ContentPage({ path }: { path: string }) {
  useScrollToAnchor()
  const page = sitePageByPath.get(path)

  if (!page) {
    return <NotFound />
  }

  return (
    <main className="aan-site min-h-screen bg-black text-white">
      <SiteHeader />
      <SitePageBody page={page} />
      <SiteFooter />
    </main>
  )
}
